import { alpha, Box, type SxProps, type Theme, Typography, useTheme } from "@mui/material";
import type { FC, ReactNode } from "react";
import { InHouseTooltip } from "@/components/InHouseTooltip/InHouseTooltip";
import { FONT_SIZE, INK, MOTION, NEUTRAL } from "@/theme/tokens";
import { prefersReducedMotion } from "@/utils/reducedMotion";

/** One switch in the set. `tone` flags a segment that needs attention. */
export type GrantBarSegment = {
	/** Stable key, also the testid suffix. */
	id: string;
	/** Is it on? Drives the fill. */
	filled: boolean;
	/** Hover text: what it does and its state. */
	tooltip?: ReactNode;
	/** `attention` = differs from the expected value; `blocked` = on but cannot work. */
	tone?: "default" | "attention" | "blocked";
};

type Props = {
	segments: readonly GrantBarSegment[];
	/** Show the `3/5` count beside the bars. */
	showCount?: boolean;
	/** Accessible name for the whole group (already translated). */
	ariaLabel: string;
	/** Segment height in px. */
	height?: number;
	/** Segment width in px; wider, shorter segments read as a level meter rather than a set of switches. */
	// DEFAULT: 6, the switch-set look every existing caller renders.
	segmentWidth?: number;
	/** Fill of an on segment with the default tone — a theme token, for a set that means "how much" rather than "is it working". */
	// DEFAULT: absent = the success green.
	fill?: string;
	/** Fill of an off segment; set, it replaces the neutral fill and its hairline, for a quiet level meter. */
	// DEFAULT: absent = the neutral fill with a hairline, so a switch set's length stays readable.
	emptyFill?: string;
	sx?: SxProps<Theme>;
	dataTestId?: string;
};

/**
 * A row of segments — one per switch in a set — filled when on, plus an `n/m` count.
 *
 * Collapses a block of boolean columns into one scannable cell, with tones to flag the ones needing
 * attention. Generic over any boolean set: the caller supplies translated tooltips and the meaning.
 */
export const SystemGrantBars: FC<Props> = ({
	segments,
	showCount = true,
	ariaLabel,
	height = 18,
	segmentWidth = 6,
	fill: onFill,
	emptyFill,
	sx,
	dataTestId,
}) => {
	const theme = useTheme();
	const reduceMotion = prefersReducedMotion();
	const filledCount = segments.filter((segment) => segment.filled).length;

	const fillFor = (segment: GrantBarSegment): string => {
		if (!segment.filled) return NEUTRAL[200];
		if (segment.tone === "attention") return theme.palette.info.main;
		if (segment.tone === "blocked") return theme.palette.warning.main;
		return onFill ?? theme.palette.success.main;
	};

	return (
		<Box
			data-testid={dataTestId}
			role="group"
			aria-label={ariaLabel}
			sx={[{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }, ...(Array.isArray(sx) ? sx : [sx])]}
		>
			<Box sx={{ display: "flex", alignItems: "center", gap: 0.375, minWidth: 0 }}>
				{segments.map((segment) => {
					const fill = fillFor(segment);
					const bar = (
						<Box
							key={segment.id}
							data-testid={dataTestId ? `${dataTestId}-${segment.id}` : undefined}
							data-filled={segment.filled}
							sx={{
								width: segmentWidth,
								height,
								borderRadius: 0.5,
								flexShrink: 0,
								backgroundColor: segment.filled ? fill : (emptyFill ?? NEUTRAL[200]),
								// Hairline on an unfilled segment so the set's length stays readable.
								border: segment.filled || emptyFill !== undefined ? "none" : `1px solid ${alpha(NEUTRAL[400], 0.6)}`,
								transition: reduceMotion ? "none" : `background-color ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}`,
							}}
						/>
					);
					return segment.tooltip ? (
						<InHouseTooltip key={segment.id} title={segment.tooltip}>
							{bar}
						</InHouseTooltip>
					) : (
						bar
					);
				})}
			</Box>
			{showCount && (
				<Typography
					component="span"
					data-testid={dataTestId ? `${dataTestId}-count` : undefined}
					sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
				>
					{filledCount}/{segments.length}
				</Typography>
			)}
		</Box>
	);
};

export default SystemGrantBars;
